import { baseTypeFromByte } from './baseTypes'
import { crc16 } from './crc'
import { FieldRangeError, FitError } from './errors'
import type { DataRecord, Definition, FieldDef, FieldValue, FitFile } from './types'

export interface EncodeOptions {
  /**
   * Ignore the passthrough shortcut and re-serialise every record from its decoded values.
   * Used by the round-trip test to prove decode→values→encode is byte-identical; production
   * export leaves it off so untouched records are emitted verbatim.
   */
  readonly forceReencode?: boolean
}

/**
 * Encode a decoded FIT file back to bytes.
 *
 * Encoder rule (one line): `!forceReencode && !dirty && raw !== null` → emit `raw` verbatim;
 * otherwise re-serialise from decoded values. This confines the diff to records we chose to
 * touch and makes an unedited round-trip byte-identical by construction.
 */
export function encode(file: FitFile, options: EncodeOptions = {}): Uint8Array {
  const force = options.forceReencode ?? false
  const chunks: Uint8Array[] = []

  for (const record of file.records) {
    if (!force && !record.dirty && record.raw !== null) {
      chunks.push(record.raw)
      continue
    }
    if (record.kind === 'definition') {
      chunks.push(serialiseDefinition(record.def))
    } else if (record.compressed) {
      // We never edit compressed-timestamp records; if reached, emit the preserved bytes.
      if (record.raw === null) {
        throw new FitError('cannot re-serialise a compressed-timestamp record from values')
      }
      chunks.push(record.raw)
    } else {
      chunks.push(serialiseData(record))
    }
  }

  const dataSize = chunks.reduce((n, c) => n + c.length, 0)

  const header = new Uint8Array(file.header.headerSize)
  const headerView = new DataView(header.buffer)
  header[0] = file.header.headerSize
  header[1] = file.header.protocolVersion
  headerView.setUint16(2, file.header.profileVersion, true)
  headerView.setUint32(4, dataSize, true)
  header[8] = 0x2e // '.'
  header[9] = 0x46 // 'F'
  header[10] = 0x49 // 'I'
  header[11] = 0x54 // 'T'
  if (file.header.hasHeaderCrc) {
    headerView.setUint16(12, crc16(header.subarray(0, 12)), true)
  }

  const body = concat([header, ...chunks])
  const fileCrc = crc16(body)
  const out = new Uint8Array(body.length + 2)
  out.set(body, 0)
  new DataView(out.buffer).setUint16(body.length, fileCrc, true)
  return out
}

function serialiseDefinition(def: Definition): Uint8Array {
  // header(1) + reserved(1) + arch(1) + global(2) + numFields(1) = 6 bytes before the fields.
  const size = 6 + def.fields.length * 3 + (def.hasDevData ? 1 + def.devFields.length * 3 : 0)
  const out = new Uint8Array(size)
  const view = new DataView(out.buffer)
  const littleEndian = def.architecture === 0

  out[0] = 0x40 | (def.hasDevData ? 0x20 : 0) | (def.localType & 0x0f)
  out[1] = def.reserved
  out[2] = def.architecture
  view.setUint16(3, def.globalMessageNumber, littleEndian)
  out[5] = def.fields.length
  let p = 6
  for (const f of def.fields) {
    out[p] = f.fieldDefNum
    out[p + 1] = f.size
    out[p + 2] = f.baseTypeByte
    p += 3
  }
  if (def.hasDevData) {
    out[p] = def.devFields.length
    p += 1
    for (const d of def.devFields) {
      out[p] = d.fieldNum
      out[p + 1] = d.size
      out[p + 2] = d.devDataIndex
      p += 3
    }
  }
  return out
}

function serialiseData(record: DataRecord): Uint8Array {
  const def = record.def
  const littleEndian = def.architecture === 0
  const bodySize =
    def.fields.reduce((n, f) => n + f.size, 0) + def.devFields.reduce((n, d) => n + d.size, 0)
  const out = new Uint8Array(1 + bodySize)
  const view = new DataView(out.buffer)

  out[0] = def.localType & 0x0f // definition/dev/compressed bits all 0 for a normal data record
  let p = 1
  for (let i = 0; i < def.fields.length; i++) {
    writeField(
      out,
      view,
      p,
      def.fields[i]!,
      record.values[i]!,
      littleEndian,
      def.globalMessageNumber,
    )
    p += def.fields[i]!.size
  }
  for (let i = 0; i < def.devFields.length; i++) {
    const blob = record.devValues[i]!
    out.set(blob, p)
    p += def.devFields[i]!.size
  }
  return out
}

function writeField(
  out: Uint8Array,
  view: DataView,
  offset: number,
  field: FieldDef,
  value: FieldValue,
  littleEndian: boolean,
  globalMessageNumber: number,
): void {
  if (value instanceof Uint8Array) {
    if (value.length !== field.size) {
      throw new FieldRangeError(
        globalMessageNumber,
        String(field.fieldDefNum),
        value.length,
        `opaque field expects ${field.size} bytes`,
      )
    }
    out.set(value, offset)
    return
  }

  const info = baseTypeFromByte(field.baseTypeByte)
  const values = typeof value === 'number' ? [value] : value
  for (let i = 0; i < values.length; i++) {
    writeInt(
      view,
      offset + i * info.size,
      values[i]!,
      info.size,
      info.signed,
      littleEndian,
      field,
      globalMessageNumber,
    )
  }
}

function writeInt(
  view: DataView,
  offset: number,
  value: number,
  size: number,
  signed: boolean,
  littleEndian: boolean,
  field: FieldDef,
  globalMessageNumber: number,
): void {
  // Mandatory range check — the last line of defence against a domain-layer overflow.
  // Fuzzing found a uint16 avg_speed overflow in 27/500 trials without it.
  if (!Number.isInteger(value)) {
    throw new FieldRangeError(
      globalMessageNumber,
      String(field.fieldDefNum),
      value,
      'is not an integer',
    )
  }
  const [min, max] = intRange(size, signed)
  if (value < min || value > max) {
    throw new FieldRangeError(
      globalMessageNumber,
      String(field.fieldDefNum),
      value,
      `out of range [${min}, ${max}]`,
    )
  }
  switch (size) {
    case 1:
      signed ? view.setInt8(offset, value) : view.setUint8(offset, value)
      break
    case 2:
      signed
        ? view.setInt16(offset, value, littleEndian)
        : view.setUint16(offset, value, littleEndian)
      break
    case 4:
      signed
        ? view.setInt32(offset, value, littleEndian)
        : view.setUint32(offset, value, littleEndian)
      break
    default:
      throw new FieldRangeError(
        globalMessageNumber,
        String(field.fieldDefNum),
        value,
        `bad size ${size}`,
      )
  }
}

function intRange(size: number, signed: boolean): [number, number] {
  if (signed) {
    const bound = 2 ** (size * 8 - 1)
    return [-bound, bound - 1]
  }
  return [0, 2 ** (size * 8) - 1]
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0)
  const out = new Uint8Array(total)
  let p = 0
  for (const part of parts) {
    out.set(part, p)
    p += part.length
  }
  return out
}
